import createDOMPurify from 'dompurify'
import type { DOMPurify as DOMPurifyInstance } from 'dompurify'

/**
 * The one place where HTML that the app did not write itself becomes markup for `v-html`.
 *
 * Task, protocol and comment bodies, help/update texts, AI suggestions and converted attachments
 * reach the browser from many writers: the editor, but also the inbound mail ingest (the
 * customer's text), document converters and any client of the upsert endpoints, which accept any
 * string. None of it may run in the app's origin, where the auth tokens sit in localStorage.
 *
 * Deliberately a strict allow-list: the formatting the editor produces (paragraphs, headings,
 * lists, emphasis, links, tables, inline images, task lists) survives, everything else is dropped.
 *  - no script/style/iframe/frame/object/embed/form/input/button/svg/math/template/meta/base/link;
 *  - no `on*` handler, no `id`/`name` (DOM clobbering);
 *  - links only to http(s), mailto, tel, a fragment or a relative path; images only from http(s),
 *    relative paths or `data:image/(png|jpeg|gif|webp)`. Never javascript:, vbscript:, data: links;
 *  - inline `style` is reduced to an allow-list of formatting properties (colour, background colour,
 *    alignment, font weight/style, text decoration; sizes only on images and table parts, numeric
 *    units only), after decoding CSS escapes (`u\72l(`) and dropping comments. Nothing that can
 *    load (`url(`), position (`position`, `inset`, `z-index`, `transform`) or run survives;
 *  - `class` keeps only the editor's own classes (mentions, code languages), so no app or
 *    Element Plus class (overlays, fixed bars) can be applied;
 *  - every remaining link opens in a new tab with `rel="noopener noreferrer"`;
 *  - a task-list checkbox becomes a ballot-box character (U+2610/U+2611) instead of an `<input>`.
 *
 * A dedicated DOMPurify instance, so its hooks never reach other DOMPurify users.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'div', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'mark', 'sub', 'sup', 'small', 'big',
  'blockquote', 'code', 'pre', 'kbd', 'abbr', 'cite', 'q',
  'a', 'img', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'label',
]

const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'width', 'height',
  'colspan', 'rowspan', 'align', 'valign', 'style', 'class',
  'start', 'reversed', 'type',
]

const FORBID_TAGS = [
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'form', 'input', 'button', 'select', 'option', 'textarea',
  'svg', 'math', 'template', 'noscript', 'noembed', 'noframes',
  'meta', 'link', 'base', 'audio', 'video', 'source', 'track', 'portal',
]

const FORBID_ATTR = ['id', 'name', 'formaction', 'action', 'xlink:href', 'srcset', 'srcdoc', 'background', 'ping']

/** http(s), mailto, tel, a fragment, or a target without a scheme (relative path, `?q`). */
const SAFE_LINK = /^(?:(?:https?|mailto|tel):|[#/?.]|[^:/?#]*(?:[/?#]|$))/i
/** Image sources: http(s) or scheme-less, plus raster data URIs. */
const SAFE_IMAGE = /^(?:https?:|[/?.]|[^:/?#]*(?:[/?#]|$))/i
const SAFE_DATA_IMAGE = /^data:image\/(?:png|jpe?g|gif|webp)[;,]/i
/** Formatting properties kept on any element. */
const STYLE_PROPS = new Set(['color', 'background-color', 'text-align', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line', 'vertical-align'])
/** Size properties, kept on images and table parts only (editor resize, table column widths). */
const SIZE_PROPS = new Set(['width', 'height', 'min-width', 'max-width'])
const SIZED_ELEMENTS = new Set(['IMG', 'TABLE', 'COL', 'COLGROUP', 'TD', 'TH'])
/** Colour/keyword values: names, numbers (font-weight), #hex, rgb()/rgba()/hsl()/hsla() with plain numbers. */
const SAFE_STYLE_VALUE = /^(?:[a-z-]+|\d{1,4}|#[0-9a-f]{3,8}|(?:rgba?|hsla?)\(\s*[\d.%\s,/]+\))(?:\s+(?:[a-z-]+|#[0-9a-f]{3,8}))*$/i
const SAFE_SIZE_VALUE = /^(?:\d+(?:\.\d+)?(?:px|%|em|rem)?|auto)$/i
/** Editor classes: mentions (`mention`, `mention--user`), code block languages. */
const SAFE_CLASS = /^(?:mention(?:--[a-z]+)?|language-[a-z0-9+#-]+)$/i

/** Resolves CSS escapes (`\72` → `r`, `\(` → `(`) and drops comments, as the CSS parser would. */
function decodeCss(value: string): string {
  return value
    .replace(/\/\*[\s\S]*?(?:\*\/|$)/g, '')
    .replace(/\\([0-9a-f]{1,6})\s?/gi, (_, hex: string) => {
      const code = parseInt(hex, 16)
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '\ufffd'
    })
    .replace(/\\([\s\S])/g, '$1')
}

/** The allowed declarations of a style attribute, or '' when none is left. */
export function filterStyle(style: string, nodeName: string): string {
  const kept: string[] = []
  for (const declaration of decodeCss(style).split(';')) {
    const colon = declaration.indexOf(':')
    if (colon < 0) continue
    const prop = declaration.slice(0, colon).trim().toLowerCase()
    const value = declaration.slice(colon + 1).trim().replace(/\s*!important$/i, '')
    if (!value) continue
    if (STYLE_PROPS.has(prop) && SAFE_STYLE_VALUE.test(value)) kept.push(`${prop}: ${value}`)
    else if (SIZE_PROPS.has(prop) && SIZED_ELEMENTS.has(nodeName) && SAFE_SIZE_VALUE.test(value)) kept.push(`${prop}: ${value}`)
  }
  return kept.join('; ')
}

/** Browsers drop control characters and whitespace inside a scheme (`java\tscript:`). */
function compactUrl(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u0020\u007f-\u009f\u00ad\u200b-\u200f\u2028\u2029\ufeff]+/g, '')
}

/** Whether a link target (`<a href>`) may stay. */
export function isSafeLinkUrl(value: string): boolean {
  return SAFE_LINK.test(compactUrl(value))
}

/** Whether an image source (`<img src>`) may stay. */
export function isSafeImageUrl(value: string): boolean {
  const url = compactUrl(value)
  if (/^data:/i.test(url)) return SAFE_DATA_IMAGE.test(url)
  return SAFE_IMAGE.test(url)
}

let instance: DOMPurifyInstance | null = null

function purifier(): DOMPurifyInstance {
  if (instance) return instance
  const purify = createDOMPurify(window)

  purify.addHook('uponSanitizeElement', (node, data) => {
    // TipTap task lists: keep the checked state as a character; the input itself is dropped.
    if (data.tagName === 'input' && node instanceof Element && node.parentNode) {
      const type = (node.getAttribute('type') ?? '').toLowerCase()
      if (type === 'checkbox') {
        const mark = node.hasAttribute('checked') ? '\u2611 ' : '\u2610 '
        node.parentNode.insertBefore(node.ownerDocument.createTextNode(mark), node)
      }
    }
  })

  purify.addHook('uponSanitizeAttribute', (node, data) => {
    const name = data.attrName
    if (name === 'href') {
      if (node.nodeName !== 'A' || !isSafeLinkUrl(data.attrValue)) data.keepAttr = false
    } else if (name === 'src') {
      if (node.nodeName !== 'IMG' || !isSafeImageUrl(data.attrValue)) data.keepAttr = false
      // Our own check is authoritative (raster data: images included).
      else data.forceKeepAttr = true
    } else if (name === 'style') {
      const style = filterStyle(data.attrValue, node.nodeName)
      if (style) data.attrValue = style
      else data.keepAttr = false
    } else if (name === 'class') {
      const classes = data.attrValue.split(/\s+/).filter((c) => SAFE_CLASS.test(c))
      if (classes.length) data.attrValue = classes.join(' ')
      else data.keepAttr = false
    } else if (name === 'type') {
      // Only list numbering (`<ol type="a">`) needs it.
      if (node.nodeName !== 'OL' && node.nodeName !== 'UL') data.keepAttr = false
    }
  })

  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName !== 'A') return
    const el = node as Element
    if (el.hasAttribute('href')) {
      el.setAttribute('target', '_blank')
      el.setAttribute('rel', 'noopener noreferrer')
    } else {
      el.removeAttribute('target')
      el.removeAttribute('rel')
    }
  })

  instance = purify
  return purify
}

/** Sanitises untrusted HTML for `v-html` (see the file comment for what survives). */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return ''
  return purifier().sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: true,
    ALLOW_ARIA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ALLOW_SELF_CLOSE_IN_ATTR: false,
    SAFE_FOR_TEMPLATES: false,
    WHOLE_DOCUMENT: false,
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
  }) as string
}

/** Escapes plain text for an HTML context (for building markup around untrusted strings). */
export function escapeHtml(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
